import { useCallback, useEffect, useRef, useState } from 'react'
import {
  exportData,
  getAttention,
  getData,
  getHealth,
  getMe,
  getVault,
  importData,
  logout,
  rdapRefreshAll,
  type ApiError,
  type AttentionItem,
  type Me,
} from './api'
import { SECTIONS, SECTION_KEYS, overdueCount } from './model'
import type { Rec, RegisterData, SectionKey } from './model'
import { Overview } from './Overview'
import { Section } from './Section'
import { Search } from './Search'
import { EditModal } from './EditModal'
import { LockModal } from './LockModal'
import { Attention } from './Attention'
import { attentionDue } from './attn'
import { Login } from './Login'
import { ChangePassword } from './ChangePassword'
import { Users } from './Users'
import { Toast } from './ui'
import { toast } from './toast'
import { vault, type VaultMeta } from './vault'
import { useVault } from './useVault'

type View = 'overview' | SectionKey | 'users'
type DateFilter = 'all' | 'over' | 'soon' | 'none'
type Modal = { mode: 'add' } | { mode: 'edit'; rec: Rec } | null

const NAV: string[] = ['overview', ...SECTION_KEYS, 'users']
const isView = (h: string): h is View => NAV.includes(h)

function readHash(): View {
  const h = window.location.hash.replace(/^#\/?/, '')
  return isView(h) ? h : 'overview'
}

export function App() {
  const [me, setMe] = useState<Me | null>(null)
  const [authState, setAuthState] = useState<'checking' | 'in' | 'out'>('checking')
  const [needsSetup, setNeedsSetup] = useState(false)
  const [data, setData] = useState<RegisterData | null>(null)
  const [dataError, setDataError] = useState<string | null>(null)
  const [provisionError, setProvisionError] = useState<string | null>(null)

  const [view, setView] = useState<View>(readHash)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<DateFilter>('all')
  const [modal, setModal] = useState<Modal>(null)
  const [vaultMeta, setVaultMeta] = useState<VaultMeta | null>(null)
  const [attn, setAttn] = useState<{ pastDue: AttentionItem[]; dueSoon: AttentionItem[] } | null>(null)
  const [showAttn, setShowAttn] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const canEdit = me?.role === 'admin'
  const vaultState = useVault()

  const reload = useCallback(async () => {
    try {
      setData(await getData())
    } catch (e) {
      setDataError((e as ApiError).message ?? 'Could not reload the register.')
    }
  }, [])

  const loadEverything = useCallback(async (m: Me) => {
    setMe(m)
    setAuthState('in')
    try {
      setData(await getData())
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'not_provisioned') setProvisionError(err.message)
      else setDataError(err.message ?? 'Could not load the register.')
    }
    if (m.role === 'admin') {
      try {
        setVaultMeta(await getVault())
      } catch {
        /* ignore */
      }
    }
    try {
      const a = await getAttention()
      setAttn(a)
      if (attentionDue(a.pastDue, a.dueSoon)) setShowAttn(true)
    } catch {
      /* ignore */
    }
  }, [])

  // initial auth check
  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const m = await getMe()
        if (live) await loadEverything(m)
      } catch {
        if (!live) return
        try {
          const h = await getHealth()
          if (live) setNeedsSetup(h.needsSetup)
        } catch {
          /* ignore */
        }
        if (live) setAuthState('out')
      }
    })()
    return () => {
      live = false
    }
  }, [loadEverything])

  // auto-lock: any activity resets the 10-minute idle timer
  useEffect(() => {
    const bump = () => vault.touch()
    for (const ev of ['mousedown', 'keydown', 'touchstart'] as const) {
      window.addEventListener(ev, bump, { passive: true })
    }
    return () => {
      for (const ev of ['mousedown', 'keydown', 'touchstart'] as const) {
        window.removeEventListener(ev, bump)
      }
    }
  }, [])

  // hash <-> view
  useEffect(() => {
    const onHash = () => setView(readHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const go = useCallback((v: View) => {
    window.location.hash = `#/${v}`
    setView(v)
    setSelectedId(null)
    setFilter('all')
    setQuery('')
  }, [])

  const openRecord = useCallback((section: SectionKey, id: string) => {
    window.location.hash = `#/${section}`
    setView(section)
    setFilter('all')
    setQuery('')
    setSelectedId(id)
  }, [])

  // "/" focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !/^(INPUT|TEXTAREA)$/.test((e.target as HTMLElement)?.tagName)) {
        e.preventDefault()
        searchRef.current?.focus()
      }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) {
        setQuery('')
        searchRef.current?.blur()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  async function runExport() {
    try {
      const dump = await exportData()
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `access-register-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(a.href)
      toast('Exported')
    } catch (e) {
      toast((e as ApiError).message ?? 'Export failed')
    }
  }

  async function runImport(file: File) {
    try {
      const payload = JSON.parse(await file.text())
      const res = await importData(payload)
      toast(`Imported: ${res.created} new, ${res.updated} updated`)
      await reload()
    } catch (e) {
      toast((e as ApiError).message ?? 'Import failed — is it a valid export file?')
    }
  }

  async function signOut() {
    try {
      await logout()
    } catch {
      /* ignore */
    }
    vault.lock()
    window.location.reload()
  }

  if (authState === 'checking') {
    return (
      <div className="center">
        <p>Loading…</p>
      </div>
    )
  }

  if (authState === 'out') {
    return <Login needsSetup={needsSetup} onSignedIn={loadEverything} />
  }

  if (me?.mustChangePassword) {
    return (
      <>
        <div className="center">
          <p>Setting up your account…</p>
        </div>
        <ChangePassword
          onDone={async () => {
            try {
              await loadEverything(await getMe())
            } catch {
              window.location.reload()
            }
          }}
        />
        <Toast />
      </>
    )
  }

  if (provisionError) {
    return (
      <div className="center">
        <div className="errbox warn">
          <h2>Your account isn&apos;t set up yet</h2>
          <p>{provisionError}</p>
          <button type="button" className="btn s" style={{ marginTop: 12 }} onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  const searching = query.trim().length > 0
  const sectionKey = view === 'overview' || view === 'users' ? null : view
  const sec = sectionKey ? SECTIONS[sectionKey] : null

  const title = searching
    ? 'Search'
    : view === 'users'
      ? 'People'
      : sec
        ? sec.label
        : 'Overview'
  const lede = searching
    ? `Matches for “${query.trim()}” across every section`
    : view === 'users'
      ? 'Who can sign in, and what they can do'
      : sec
        ? sec.lede
        : 'What needs attention, and what renews when.'

  return (
    <div className="app">
      <aside>
        <div className="brand">
          <div className="mark">A</div>
          <div>
            <b>Access Register</b>
            <span>Analytix</span>
          </div>
        </div>
        <nav>
          <button
            type="button"
            aria-current={view === 'overview' && !searching}
            onClick={() => go('overview')}
          >
            Overview
          </button>
          {SECTION_KEYS.map((k) => {
            const over = data ? overdueCount(data, k) : 0
            return (
              <button
                key={k}
                type="button"
                aria-current={view === k && !searching}
                onClick={() => go(k)}
              >
                {SECTIONS[k].label}
                {over > 0 && <em className="badge">{over}</em>}
              </button>
            )
          })}
          {canEdit && (
            <button
              type="button"
              aria-current={view === 'users' && !searching}
              onClick={() => go('users')}
            >
              People
            </button>
          )}
        </nav>
        <div className="foot">
          <b>{me?.name ?? me?.email}</b>
          <span>{canEdit ? 'Administrator' : 'Viewer'}</span>
          <div className="footactions">
            {canEdit && (
              <>
                <button type="button" onClick={runExport}>
                  Export data
                </button>
                <button type="button" onClick={() => fileRef.current?.click()}>
                  Import data
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) runImport(f)
                    e.target.value = ''
                  }}
                />
                {vaultState.unlocked && (
                  <button
                    type="button"
                    onClick={() => {
                      vault.lock()
                      toast('Locked')
                    }}
                  >
                    Lock passwords now
                  </button>
                )}
              </>
            )}
            <button type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main>
        <div className="top">
          <div>
            <h1>{title}</h1>
            <p className="lede">{lede}</p>
          </div>
          <div className="spacer" />
          {view !== 'users' && (
            <div className="search">
              <input
                ref={searchRef}
                type="search"
                placeholder="Search everything"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <kbd>/</kbd>
            </div>
          )}
          {view === 'domains' && canEdit && !searching && (
            <button
              type="button"
              className="btn"
              onClick={async () => {
                toast('Checking every domain against RDAP…')
                try {
                  const r = await rdapRefreshAll()
                  toast(
                    `RDAP: ${r.updated.length} updated, ${r.unchanged.length} confirmed, ${r.skipped.length} no data`,
                  )
                  reload()
                } catch (e) {
                  toast((e as ApiError).message ?? 'RDAP refresh failed')
                }
              }}
            >
              Refresh from RDAP
            </button>
          )}
          {sectionKey && canEdit && !searching && (
            <button type="button" className="btn p" onClick={() => setModal({ mode: 'add' })}>
              Add
            </button>
          )}
        </div>

        <div className="scroll">
          {view === 'users' ? (
            <Users meEmail={me?.email ?? ''} />
          ) : dataError ? (
            <p className="empty">{dataError}</p>
          ) : !data ? (
            <p className="empty">No data.</p>
          ) : searching ? (
            <Search data={data} query={query} onOpen={openRecord} />
          ) : sectionKey && sec ? (
            <Section
              section={sec}
              rows={data[sectionKey]}
              selectedId={selectedId}
              onSelect={setSelectedId}
              filter={filter}
              onFilter={setFilter}
              query=""
              canEdit={!!canEdit}
              onEdit={(rec) => setModal({ mode: 'edit', rec })}
              onChanged={reload}
            />
          ) : (
            <Overview data={data} onOpen={openRecord} onGoSection={(s) => go(s)} />
          )}
        </div>
      </main>

      {modal && sec && (
        <EditModal
          key={modal.mode === 'edit' ? `edit-${modal.rec.id}` : 'add'}
          section={sec}
          record={modal.mode === 'edit' ? modal.rec : null}
          onClose={() => setModal(null)}
          onSaved={reload}
        />
      )}

      {showAttn && attn && (
        <Attention
          pastDue={attn.pastDue}
          dueSoon={attn.dueSoon}
          onOpen={openRecord}
          onClose={() => setShowAttn(false)}
        />
      )}

      {vaultState.needsUnlock && vaultMeta && (
        <LockModal
          meta={vaultMeta}
          onClose={() => {
            if (vault.needsUnlock) vault.cancelUnlock()
          }}
          onVaultCreated={() => {
            setVaultMeta((m) => (m ? { ...m, exists: true } : { exists: true }))
            reload()
          }}
        />
      )}

      <Toast />
    </div>
  )
}
